import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/shared/components/ui/avatar";
import { CheckCircle, MailOpen, Send, XCircle } from "lucide-react";
import { TableRowDetailsItem } from "../types";
import useProductSupplierResponsesGridTemplate from "../utils/useProductSupplierResponsesGridTemplate";
import ProductRowActions from "./product-row-actions/product-row-actions";
import ProcRightLogo from "@/shared/components/icons/procright-logo";

function StatusBadge({ status }: { status: TableRowDetailsItem["status"] }) {
  const statusConfig = {
    replied: {
      label: "Yanıtladı",
      bgColor: "bg-[rgba(22,163,74,0.1)]",
      borderColor: "border-[rgba(22,163,74,0.2)]",
      textColor: "text-green-600",
      icon: CheckCircle,
      iconColor: "#16a34a",
    },
    opened: {
      label: "Açıldı",
      bgColor: "bg-[rgba(59,130,246,0.1)]",
      borderColor: "border-[rgba(59,130,246,0.2)]",
      textColor: "text-blue-500",
      icon: MailOpen,
      iconColor: "#3b82f6",
    },
    sent: {
      label: "Gönderildi",
      bgColor: "bg-white",
      borderColor: "border-border",
      textColor: "text-foreground",
      icon: Send,
      iconColor: "#09090b",
    },
    cancelled: {
      label: "İptal edildi",
      bgColor: "bg-secondary",
      borderColor: "border-border",
      textColor: "text-foreground",
      icon: XCircle,
      iconColor: "#09090b",
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  return (
    <div
      className={`${config.bgColor} ${config.borderColor} border h-5 max-h-[20px] rounded-full shrink-0 flex items-center justify-center px-[6px] py-1`}
    >
      <div className="flex items-center gap-0">
        <div className="flex items-center mr-[4px]">
          <Icon size={10} color={config.iconColor} />
        </div>
        <span
          className={`${config.textColor} text-[11px] font-600 leading-4 whitespace-pre`}
        >
          {config.label}
        </span>
      </div>
    </div>
  );
}

export default function TableRowProductSupplierResponsesRow({
  item,
}: {
  item: TableRowDetailsItem;
}) {
  const progressPercentage = item.progress.percentage;

  return (
    <div className="last:border-b-0 border-b border-border">
      <div
        className="grid items-center h-[56px] relative w-full"
        style={{
          gridTemplateColumns: useProductSupplierResponsesGridTemplate(),
        }}
      >
        {/* User Column */}
        <div className="flex items-center gap-2 h-full pr-4 pl-[40px] py-0 relative overflow-hidden">
          {item.user.isAI ? (
            <div className="size-[32px] rounded-full bg-white border border-border text-[12px] flex items-center justify-center rotate-90">
              <ProcRightLogo />
            </div>
          ) : (
            <div className="size-[32px] rounded-full bg-[#F4F4F5] text-[12px] flex items-center justify-center">
              SK
            </div>
          )}
          <div className="flex flex-col items-start shrink-0 min-w-0">
            <p className="text-[14px] font-600 text-foreground truncate">
              {item.user.name}
            </p>
            {item.user.email && (
              <p className="text-[11px] font-500 text-muted-foreground truncate">
                {item.user.email}
              </p>
            )}
          </div>
        </div>

        {/* Progress Column */}
        <div className="flex flex-col gap-1 items-center justify-center h-full py-0 relative">
          <div className="flex items-start gap-1 shrink-0 w-full">
            <p className="text-sm font-600 text-foreground leading-5 whitespace-pre">
              {item.progress.current}/{item.progress.total}
            </p>
            <div className="flex flex-col h-[18px] justify-center leading-0 shrink-0 w-1 text-[#5e5e60] text-[13px]">
              <p className="leading-normal">·</p>
            </div>
            <p className="text-sm font-500 text-foreground leading-5 whitespace-pre">
              %{progressPercentage}
            </p>
          </div>
          <div className="flex gap-[10px] h-3 items-center shrink-0 w-full">
            <div className="grow h-2 min-h-px min-w-px overflow-clip relative rounded-full shrink-0 bg-muted">
              <div
                className={`absolute top-0 left-0 bottom-0 rounded-br-md rounded-tr-md transition-all ${
                  progressPercentage === 100
                    ? "bg-green-600"
                    : progressPercentage > 0
                    ? "bg-zinc-900"
                    : "bg-transparent"
                }`}
                style={{
                  width: `${progressPercentage}%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Count Column */}
        <div className="flex items-center justify-center h-full px-2 py-0 relative">
          <p className="text-sm font-500 text-foreground leading-5 whitespace-pre text-center">
            {item.count}
          </p>
        </div>

        {/* Time 1 Column */}
        <div className="flex items-center justify-center h-full px-2 py-0 relative">
          <p className="text-sm font-500 text-foreground leading-5 whitespace-pre text-center">
            {item.time1}
          </p>
        </div>

        {/* Time 2 Column */}
        <div className="flex items-center justify-center h-full px-2 py-0 relative">
          <p className="text-sm font-500 text-foreground leading-5 whitespace-pre text-center">
            {item.time2}
          </p>
        </div>

        {/* Status Column */}
        <div className="flex items-center justify-center h-full px-4 py-0 relative">
          <StatusBadge status={item.status} />
        </div>

        {/* Actions Column */}
        <div className="flex items-center justify-end h-[56px] py-0 relative">
          <div className="flex items-center justify-center px-[8px] h-full relative shrink-0 w-[41px]">
            <ProductRowActions status={item.status} item={item} />
          </div>
        </div>
      </div>
    </div>
  );
}
